/**
 * What the DATABASE will accept from a customer form, checked before we ask it.
 *
 * WHY THIS IS ITS OWN FILE
 * This lived inside pages/Customers.js as a module-local `validateCustomerFields`, which meant
 * it protected the Customers page and nothing else. `components/AddCustomerModal.js` — the
 * modal onboarding's Go Live step opens — called `createOrLinkCustomer` with no validation at
 * all (board #465), so the same two rules below failed there in the two silent ways they were
 * written to prevent. Moving it rather than copying it is the point: a second copy is how the
 * email pattern came to exist in three places at once.
 *
 * THE TWO RULES, and why each is server-side-only without this:
 *
 *   email — CHECK constraint `chk_customers_email_format` on public.customers requires a TLD:
 *           `someone@local` is REJECTED with 23514 while `someone@local.com` is fine and NULL
 *           is fine. `<input type="email">` does not enforce a TLD (the HTML5 spec
 *           deliberately allows intranet addresses), so the browser passes it and Postgres
 *           refuses it at the end of a filled-in form. utils/email.js owns the pattern,
 *           transcribed from the live constraint; it is imported here rather than restated,
 *           which retires the third copy of that regex.
 *
 *   phone — the `handle_customer_changes` BEFORE trigger runs every value through
 *           `format_phone_e164`, which returns NULL when the input has no usable digits. So
 *           typing letters never "got accepted": the phone was silently DISCARDED on save and
 *           the customer was created looking successful. utils/phone.js owns that question.
 *
 *   name  — CHECK constraint `chk_customer_name` on public.customers requires at least one of
 *           first_name, last_name or company_name to be non-blank AFTER TRIM, so a name of
 *           spaces is refused with 23514. The forms mark those fields `required`, but HTML
 *           `required` is satisfied by "   " — it tests emptiness, not content — so a
 *           whitespace-only name passed the browser and died at the database, which is the
 *           same shape as the two rules above (board #573).
 *
 * Returns a { field: message } object; empty means valid.
 */
import { isValidEmail } from './email';
import { isPlausiblePhone, phoneErrorMessage } from './phone';

export const validateCustomerFields = (formData = {}) => {
  const errors = {};

  // Mirrors chk_customer_name. Reported against the field the form asked this customer for:
  // a business is identified by its company name, a person by their own, so pointing a
  // commercial record at First Name would name a box the user was never expected to fill.
  const blank = (v) => String(v || '').trim() === '';
  const isBusiness = ['commercial', 'industrial', 'government'].includes(formData.type);
  if (blank(formData.first_name) && blank(formData.last_name) && blank(formData.company_name)) {
    errors[isBusiness ? 'company_name' : 'first_name'] =
      'Enter a first name, last name or company name.';
  } else if (!isBusiness && blank(formData.first_name) && blank(formData.last_name)) {
    // #5970: a residential customer is a person, and the form marks First/Last as required.
    // A business name alone passed, so a homeowner could be saved with no name at all (every
    // live residential customer has one, measured 2026-10-18, so this blocks nothing existing).
    errors.first_name = "Enter the customer's first or last name.";
  }

  // #5971: an email typed into a name box became the customer's display name everywhere —
  // the DB's generated name prefers company_name — and adding the real name later could not
  // replace it.
  const looksLikeEmail = (v) => /\S+@\S+\.\S+/.test(String(v || '').trim());
  ['first_name', 'last_name', 'company_name'].forEach((field) => {
    if (!errors[field] && looksLikeEmail(formData[field])) {
      errors[field] = 'That looks like an email address — put it in the Email field.';
    }
  });

  const email = String(formData.email || '').trim();
  if (email && !isValidEmail(email)) {
    errors.email = 'Enter a complete email address, including a domain ending like .com';
  }

  // Board #1361. Both of these shared one constant reading "digits only, 10 for a US number",
  // which was printed whatever country the picker beside the box was set to. Telling somebody who
  // has selected +44 that the number is 10 digits is not a cosmetic slip — it is instructions to
  // enter the wrong thing. phoneErrorMessage derives the country from the value itself (the
  // control composes it as callingCode + digits, so the front of the string IS the chosen
  // country) and quotes a real example for it. It lives in utils/phone.js beside
  // isPlausiblePhone on purpose: the explanation has to agree with the rule, and that only holds
  // if one file owns both.
  const phone = String(formData.phone || '').trim();
  if (phone && !isPlausiblePhone(phone)) {
    errors.phone = phoneErrorMessage(phone);
  }

  const mobile = String(formData.mobile_phone || '').trim();
  if (mobile && !isPlausiblePhone(mobile)) {
    errors.mobile_phone = phoneErrorMessage(mobile);
  }

  return errors;
};

export default validateCustomerFields;
